import React, { useState } from "react";
import {
  TextField, Select, MenuItem, FormControl, RadioGroup, FormControlLabel, Radio,
  Checkbox, Typography, Card, CardContent, Button, Box, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, CircularProgress
} from "@mui/material";

const BikeForm = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [formData, setFormData] = useState({
    cc: "150",
    fiv: "",
    ncb: 0,
    riskOptions: { 
        flood: false, 
        earthquake: false,
        avls: true 
    },
    vehicleNumber: "",
    ownerName: "",
    registrationDoc: null,
    previousInsuranceDoc: null, // New field for NCB logic
    engineNumber: "",
    chassisNumber: "",
    mfgYear: "",
    cc_capacity: "",
    isScanned: false
  });

  const baseTPFromCC = (cc) => {
    const n = parseInt(cc) || 0;
    if (n <= 150) return 200;
    if (n <= 250) return 275;
    return 350;
  };

  const motorLiabilityPremiumFromCC = (cc) => {
    const n = parseInt(cc) || 0;
    if (n <= 150) return 500;
    if (n <= 250) return 650;
    return 800;
  };

  const calculatePremium = (data) => {
    const fiv = parseFloat(data.fiv) || 0;
    const basicPremium = baseTPFromCC(data.cc);
    const flvAdd = fiv * 0.0165;
    
    const floodCost = data.riskOptions.flood ? fiv * 0.0025 : 0;
    const eqCost = data.riskOptions.earthquake ? fiv * 0.0025 : 0;

    const avlsBase = basicPremium + flvAdd + floodCost + eqCost;
    const avls = data.riskOptions.avls ? avlsBase * 0.10 : 0;

    const odPremium = avlsBase - avls;
    const ncbLess = odPremium * (parseFloat(data.ncb) / 100);
    const subTotal = odPremium - ncbLess;

    const motorLiability = motorLiabilityPremiumFromCC(data.cc);
    const driverCost = 250; 
    const passengerCost = 225; 
    const staffCost = 0;

    const netPremium = subTotal + motorLiability + driverCost + passengerCost + staffCost;
    const vatAmount = netPremium * 0.15;
    const grossPremium = Math.round(netPremium + vatAmount);

    return {
      BASIC_PREMIUM: basicPremium,
      OD_PERCENT: "1.65%",
      FLV_ADD: flvAdd,
      RSD_COST: 0, 
      FLOOD_COST: floodCost,
      EQ_COST: eqCost,
      AVLS: avls,
      OD_PREMIUM: odPremium,
      NCB_LESS: ncbLess,
      SUB_TOTAL: subTotal,
      MOTOR_LIABILITY: motorLiability,
      PASSENGER_COST: passengerCost,
      DRIVER_COST: driverCost,
      STAFF_COST: staffCost,
      NET_PREMIUM: netPremium,
      VAT_AMOUNT: vatAmount,
      GROSS_PREMIUM: grossPremium
    };
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({ ...prev, riskOptions: { ...prev.riskOptions, [name]: checked } }));
    } else if (name === "registrationDoc") {
      const file = files[0];
      setFormData(prev => ({ ...prev, registrationDoc: file }));
      handleRegistrationDoc(file);
    } else if (name === "previousInsuranceDoc") {
      setFormData(prev => ({ ...prev, previousInsuranceDoc: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRegistrationDoc = async (file) => {
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append("registrationDoc", file);
    try {
      //const response = await fetch("http://localhost/bgic-store/admin/scan_registrationdoc.php", {
      const response = await fetch("https://www.bgicdigital.com/admin/scan_registrationdoc.php", {
        method: "POST",
        body: uploadData,
      });
      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          vehicleNumber: result.data.reg_no || "",
          engineNumber: result.data.engine_no || "",
          chassisNumber: result.data.chassis_no || "",
          mfgYear: result.data.mfg_year || "",
          cc_capacity: result.data.cc_capacity || "",
          vehicle_class: result.data.vehicle_class || "",
          isScanned: true
        }));
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
  e.preventDefault();
  
  // 1. Run the calculations
  //const calc = calculatePremium(formData);
  
  // 2. Send a clean, labeled object to the parent/cart
  onSubmit({
    productType: "MC Insurance",
    data: {
      ...formData,
      ...calc, // This merges all the BASIC_PREMIUM, GROSS_PREMIUM, etc.
    },
  });
};


  const calc = calculatePremium(formData);

  return (
    <form onSubmit={handleSubmit}>
      

    <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Motor Cycle Insurance (Comprehensive)
            </Typography>
        
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              Please complete the form below to proceed.
            </Typography>
          </Box>
        
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setOpenDetails(true)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 500,
            }}
          >
            View Details
          </Button>
        </Box>


      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Engine Capacity (CC)</Typography>
          <RadioGroup name="cc" value={formData.cc} onChange={handleChange} row>
            <FormControlLabel value="150" control={<Radio />} label="Up to 150 CC" />
            <FormControlLabel value="250" control={<Radio />} label="151–250 CC" />
            <FormControlLabel value="350" control={<Radio />} label="251–350 CC" />
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField label="Vehicle Sum Insured (Tk.)" type="number" name="fiv" value={formData.fiv} onChange={handleChange} fullWidth required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">No Claim Bonus (NCB)</Typography>
            <Select name="ncb" value={formData.ncb} onChange={handleChange}>
              <MenuItem value={0}>0% (New Policy)</MenuItem>
              <MenuItem value={15}>15% (2nd Year)</MenuItem>
              <MenuItem value={20}>20% (3rd Year)</MenuItem>
              <MenuItem value={25}>25% (4th Year+)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2">Discounts & Optional Risks</Typography>
          <FormControlLabel control={<Checkbox name="avls" checked={formData.riskOptions.avls} onChange={handleChange} />} label="AVLS Discount (10%)" />
          <FormControlLabel control={<Checkbox name="flood" checked={formData.riskOptions.flood} onChange={handleChange} />} label="Flood & Cyclone" />
          <FormControlLabel control={<Checkbox name="earthquake" checked={formData.riskOptions.earthquake} onChange={handleChange} />} label="Earthquake" />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1">Upload Registration Document {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}</Typography>
          <input type="file" name="registrationDoc" onChange={handleChange} required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/>
        </Grid>

        {/* Previous Insurance Doc Logic: Only show if NCB > 0 */}
        {formData.ncb > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Upload Previous Insurance Document</Typography>
            <input type="file" name="previousInsuranceDoc" onChange={handleChange} required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/>
          </Grid>
        )}

        {formData.isScanned && (
          <>
            <Grid item xs={12} md={6}><TextField label="Reg No" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/></Grid>
            <Grid item xs={12} md={6}><TextField label="Owner Name" name="ownerName" value={formData.ownerName} onChange={handleChange} fullWidth required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Engine No" name="engineNumber" value={formData.engineNumber} onChange={handleChange} fullWidth required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Chassis No" name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} fullWidth required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="CC Capacity" name="cc_capacity" value={formData.cc_capacity} onChange={handleChange} fullWidth required  sx={{"& .MuiFormLabel-asterisk": {color: "red",},}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Vehicle Class" name="vehicle_class" value={formData.vehicle_class} onChange={handleChange} fullWidth   /></Grid>
          </>
        )}

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mt: 2 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr><th colSpan="4" style={{ textAlign: "center", background: "#d9d9d9", fontWeight: 700, padding: "4px 6px", border: "1px solid #999" }}>PREMIUM COMPUTATION</th></tr>
              </thead>
              <tbody>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Basic Premium</td><td width="30" style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.BASIC_PREMIUM}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Add: 1.65% on F.I.V.</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.FLV_ADD.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>&nbsp;&nbsp;Flood & Cyclone (0.25% on F.I.V.)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.FLOOD_COST.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>&nbsp;&nbsp;Earthquake (0.25% on F.I.V.)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.EQ_COST.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>AVLS Discount (10%)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999", color: 'red' }}>-{calc.AVLS.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr style={{ fontWeight: 'bold' }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Own damage Premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.OD_PREMIUM.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Less: NCB {formData.ncb}%</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999", color: 'red' }}>-{calc.NCB_LESS.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr style={{ background: "#f9f9f9", fontWeight: "bold" }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Sub Total</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.SUB_TOTAL.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Motor liability premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.MOTOR_LIABILITY}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Passenger</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.PASSENGER_COST}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Paid Driver</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.DRIVER_COST}</td></tr>
                <tr style={{ fontWeight: 'bold' }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Net Premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.NET_PREMIUM.toFixed(2)}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Add: VAT 15%</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.VAT_AMOUNT.toFixed(2)}</td></tr>
                <tr style={{ fontWeight: "bold", background: "#eee" }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Gross Premium :</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.GROSS_PREMIUM}</td></tr>
              </tbody>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Button type="submit" variant="contained" fullWidth sx={{ mt: 3, py: 1.5 }}>Add to Cart (Tk. {calc.GROSS_PREMIUM})</Button>

      {/* Terms Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Insurance Coverage & Terms</DialogTitle>
         <DialogContent dividers>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Coverage Details:</strong>
                  </Typography>
                  <ul>
                    <li>Accidental Damage & Theft (1.65% of FIV) — Mandatory coverage</li>
                    <li>Riot & Strike Damage (0.50% of FIV) — Optional</li>
                    <li>Flood & Cyclone (0.25% of FIV) — Optional</li>
                    <li>Earthquake (0.25% of FIV) — Optional</li>
                    <li>Passenger Liability: ৳45 per passenger</li>
                    <li>Driver Liability: ৳30</li>
                  </ul>
        
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Terms & Conditions:</strong>
                  </Typography>
                  <ul>
                    <li>NCB applies only for renewals with no claims in the previous year.</li>
                    <li>Coverage is valid for one year from the start date.</li>
                    <li>Insurance coverage begins from the next day of purchase.</li>
                    <li>Excludes deliberate damage, driving without license, and non-disclosed modifications.</li>
                  </ul>
        
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Claiming Process:</strong>
                  </Typography>
                  <ol>
                    <li>Inform the insurer within 24 hours of an incident.</li>
                    <li>Submit required documents (claim form, FIR, repair invoice, previous insurance docs).</li>
                    <li>Survey and verification by insurance representative.</li>
                    <li>Claim settlement typically within 15 working days after approval.</li>
                  </ol>
                </DialogContent>
        <DialogActions><Button onClick={() => setOpenDetails(false)} color="primary" variant="contained">Got It</Button></DialogActions>
      </Dialog>
    </form>
  );
};

export default BikeForm;